import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useFetchCurrentEmergencyState } from '~/hooks/api/useFetchCurrentEmergencyState';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import { IntlShape, useIntl } from 'react-intl';
import { EmergencyDto, PublisherEmergencyReportDto } from '~/generated';
import { useSettingsByCategory } from '~/hooks/api/useSettingsByCategory';
import { getPhoneNumberUri, toNational } from '~/utils/phone-number-helpers';
import Spacings from '~/modules/ui-kit/spacings';
import ExitEmergencyModeButton from '../ExitEmergencyModeButton';
import messages from './messages';
import StatusEditButton from './StatusEditButton';
import { PublisherEmergencyStatus } from '~/utils/publisher-emergency-status';

/**
 * TODOs:
 * - format phone numbers
 */

type StatusMessageKey = 'Unknown' | 'Acknowledged' | 'Safe';

const getStatusMessageKey = (
  status?: PublisherEmergencyReportDto['status']
): StatusMessageKey => {
  switch (status) {
    case PublisherEmergencyStatus.Acknowledged:
      return 'Acknowledged';
    case PublisherEmergencyStatus.Safe:
      return 'Safe';
    case PublisherEmergencyStatus.Unknown:
    default:
      return 'Unknown';
  }
};

const columns = (
  intl: IntlShape,
  defaultRegionCallingCode?: string
): GridColDef[] => [
  {
    field: 'publisherFullName',
    headerName: intl.formatMessage(messages.fullName),
    valueGetter: (value, row: PublisherEmergencyReportDto) =>
      row.publisher?.name,
    flex: 1.5,
    minWidth: 150,
  },
  {
    field: 'status',
    headerName: intl.formatMessage(messages.status),
    flex: 1.5,
    minWidth: 140,
    valueGetter: (value, row: PublisherEmergencyReportDto) => row.status,
    renderCell: (params: GridRenderCellParams<PublisherEmergencyReportDto>) => {
      const status = params.row.status ?? PublisherEmergencyStatus.Unknown;
      const statusLabel = intl.formatMessage(
        messages[getStatusMessageKey(status)]
      );

      return (
        <StatusEditButton
          publisherEmergencyReportId={params.row.id ?? 0}
          currentStatus={status}
          statusLabel={statusLabel}
        />
      );
    },
  },
  {
    field: 'statusUpdates',
    headerName: intl.formatMessage(messages.statusMessage),
    valueGetter: (value, row: PublisherEmergencyReportDto) =>
      row.statusUpdates?.[row.statusUpdates.length - 1]?.message,
    flex: 2,
    minWidth: 150,
  },
  {
    field: 'routeName',
    headerName: intl.formatMessage(messages.routeName),
    renderCell: (
      params: GridRenderCellParams<PublisherEmergencyReportDto, string>
    ) => (
      <Spacings.Stack scale="xs">
        {params.row.assignments?.map((assignment) => (
          <div key={assignment.beginsAt}>
            <Typography variant="subtitle2">
              {moment(assignment.beginsAt).format('LT')} -{' '}
              {moment(assignment.endsAt).format('LT')}
            </Typography>
            <Typography variant="body2">{assignment.routeName}</Typography>
          </div>
        ))}
      </Spacings.Stack>
    ),
    flex: 1.2,
    minWidth: 120,
  },
  {
    field: 'publisherPhone',
    headerName: intl.formatMessage(messages.phone),
    flex: 1.2,
    minWidth: 120,
    valueGetter: (value, row: PublisherEmergencyReportDto) =>
      row.publisher?.phoneNumber,
    renderCell: (params: GridRenderCellParams<EmergencyDto, string>) => (
      <a href={params.value ? getPhoneNumberUri(params.value) : ''}>
        {params.value
          ? toNational(params.value, defaultRegionCallingCode)
          : intl.formatMessage(messages.noPhoneNumberGiven)}
      </a>
    ),
  },
  {
    field: 'publisherEmail',
    headerName: intl.formatMessage(messages.email),
    flex: 2,
    minWidth: 200,
    sortable: false,
    valueGetter: (value, row: PublisherEmergencyReportDto) =>
      row.publisher?.emailAddress,
    renderCell: (params: GridRenderCellParams<EmergencyDto, string>) => (
      <a href={`mailto:${params.value}`}>{params.value}</a>
    ),
  },
];

const EmergencyModeList = () => {
  const intl = useIntl();
  const { data } = useFetchCurrentEmergencyState();
  const { data: masterData } = useSettingsByCategory('masterdata');
  return (
    <Box sx={{ p: 2, width: '100%', overflowX: 'auto' }}>
      <Stack spacing={2}>
        {data ? (
          <DataGrid
            getRowHeight={() => 'auto'}
            sx={{
              '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
                py: '8px',
              },
              '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
                py: '15px',
              },
              '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': {
                py: '22px',
              },
            }}
            getRowId={(row) => row.id}
            rows={data?.publisherEmergencyReports || []}
            columns={columns(
              intl,
              masterData?.defaultRegionCallingCode as string | undefined
            )}
            disableRowSelectionOnClick
          />
        ) : null}
        <div>
          <ExitEmergencyModeButton />
        </div>
      </Stack>
    </Box>
  );
};

export default EmergencyModeList;
